// Members screen — Phase 4 Plan 04-01 Task 3 (D-78, DIS-04); migrated to
// telegram-ui in Phase 12 Plan 12-08 (UIR-07, UI-SPEC §7 Members).
//
// Three sub-views:
//   1. List view — telegram-ui List/Section/Cell per member (Avatar) + active
//                  invites; sticky bottom "+ Invite dispatcher" Button (owner only).
//   2. Invite modal — three-state machine (form → generating → showing_qr):
//        - form: empty (no fields per UI-SPEC; invite is always dispatcher)
//        - generating: spinner
//        - showing_qr: QR (240×240 via qrcode.react) + copyable deep_link +
//                      [Open in Telegram] (window.Telegram.WebApp.openTelegramLink)
//
// Role gate (UX-only — server enforces via require_role(["owner"])):
//   useUserRole() decodes the JWT payload (canonical lib/jwt.ts) to determine the
//   current user role. Client-side gate only; the authoritative check is on the
//   backend (T-4-INVITE-MC dispatch). If decoding fails, the user is treated as a
//   "viewer" — they see the list but no mutation buttons. (Security V4 — gating
//   must not regress through the migration.)
//
// Sources:
//   - 04-UI-SPEC §Screen 8 (Members wireframe + invite modal layout)
//   - 12-UI-SPEC §7 Members (List+Cell+Avatar; empty → Placeholder)

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Cell,
  Input,
  List,
  Placeholder,
  Section,
  Spinner,
} from "@telegram-apps/telegram-ui";
import { QRCodeSVG } from "qrcode.react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type CompanyInviteCreateOneTime,
  type CompanyInviteResponse,
  type MemberListItem,
  type MembersListResponse,
  membersApi,
} from "../api/members";
import { useAuth } from "../auth/useAuth";
import { decodeJwtPayload } from "../lib/jwt";
import { SPACING } from "../lib/tokens";

// --------------------------------------------------------------------------- //
// JWT role decoder (client-side UX gate only — server is authoritative).
// Consolidated onto lib/jwt.ts (12-08 tech-debt cleanup).
// --------------------------------------------------------------------------- //

function useUserRole(): {
  role: string | null;
  userId: number | null;
} {
  const { jwt } = useAuth();
  return useMemo(() => {
    const payload = decodeJwtPayload(jwt);
    return {
      role: payload?.role ?? null,
      userId: payload?.sub ? Number.parseInt(payload.sub, 10) : null,
    };
  }, [jwt]);
}

function acronymFor(name: string | null, id: number): string {
  if (!name || name.trim() === "") return `#${id}`.slice(0, 2);
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase() || `#${id}`.slice(0, 2);
}

// --------------------------------------------------------------------------- //
// Invite modal state machine
// --------------------------------------------------------------------------- //

type InviteModalState =
  | { kind: "closed" }
  | { kind: "form" }
  | { kind: "generating" }
  | { kind: "showing_qr"; data: CompanyInviteCreateOneTime };

// --------------------------------------------------------------------------- //
// Members screen
// --------------------------------------------------------------------------- //

export function Members(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const { role: currentRole, userId: currentUserId } = useUserRole();
  const queryClient = useQueryClient();
  const isOwner = currentRole === "owner";

  const [inviteState, setInviteState] = useState<InviteModalState>({
    kind: "closed",
  });

  // ----- queries -----

  const listQuery = useQuery<MembersListResponse>({
    queryKey: ["members"],
    queryFn: () => membersApi.list(apiClient),
    retry: 1,
  });

  // ----- mutations -----

  const createInviteMutation = useMutation({
    mutationFn: () => membersApi.createInvite(apiClient, "dispatcher"),
    onMutate: () => setInviteState({ kind: "generating" }),
    onSuccess: (data) => {
      setInviteState({ kind: "showing_qr", data });
      // Refresh list to include new invite
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
    onError: () => setInviteState({ kind: "form" }),
  });

  const revokeMutation = useMutation({
    mutationFn: (inviteId: number) => membersApi.revokeInvite(apiClient, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => membersApi.removeMember(apiClient, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });

  // ----- handlers -----

  const onOpenInvite = (): void => setInviteState({ kind: "form" });
  const onCloseInvite = (): void => setInviteState({ kind: "closed" });
  const onGenerate = (): void => {
    createInviteMutation.mutate();
  };
  const onCopyLink = (link: string): void => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(link).catch(() => {
          /* ignore — fallback selection */
        });
      }
    } catch {
      /* ignore */
    }
  };
  const onOpenInTelegram = (link: string): void => {
    const tg = (
      globalThis as { Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void } } }
    ).Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(link);
    } else if (typeof window !== "undefined") {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  // ----- render -----

  if (listQuery.isLoading) {
    return (
      <List data-testid="members-loading">
        <Placeholder header={t("dispatcher:members.loading", "Loading…") as string} />
      </List>
    );
  }

  if (listQuery.isError) {
    return (
      <List data-testid="members-error">
        <Placeholder header={t("dispatcher:members.error", "Failed to load members.") as string} />
      </List>
    );
  }

  const data = listQuery.data ?? { members: [], invites: [] };
  const openInvites = data.invites.filter((i) => i.revoked_at === null && i.accepted_at === null);

  return (
    <List data-testid="members-screen" style={{ paddingBottom: 88 }}>
      <Section
        data-testid="members-list"
        header={t("dispatcher:members.members_heading", "Members") as string}
      >
        {data.members.length === 0 ? (
          <Placeholder
            data-testid="members-empty"
            header={t("dispatcher:members.empty", "No members yet.") as string}
            description={
              t(
                "dispatcher:members.empty_body",
                "Invite a dispatcher by QR code to get started.",
              ) as string
            }
          />
        ) : (
          data.members.map((m) => (
            <MemberCell
              key={m.user_id}
              member={m}
              isOwner={isOwner}
              isSelf={m.user_id === currentUserId}
              onRemove={() => {
                if (m.user_id !== currentUserId && m.role !== "owner") {
                  removeMutation.mutate(m.user_id);
                }
              }}
            />
          ))
        )}
      </Section>

      <Section
        data-testid="members-invites"
        header={t("dispatcher:members.invites_heading", "Outstanding invites") as string}
      >
        {openInvites.length === 0 ? (
          <Cell data-testid="members-no-invites">
            {t("dispatcher:members.no_invites", "No outstanding invites.")}
          </Cell>
        ) : (
          openInvites.map((inv) => (
            <InviteCell
              key={inv.id}
              invite={inv}
              isOwner={isOwner}
              onRevoke={() => revokeMutation.mutate(inv.id)}
            />
          ))
        )}
      </Section>

      {isOwner && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: SPACING.md,
            background: "var(--tgui--bg_color, white)",
            borderTop: "1px solid var(--tgui--hint_color, #E2E8F0)",
          }}
        >
          <Button size="l" stretched data-testid="members-invite-button" onClick={onOpenInvite}>
            {t("dispatcher:members.invite_button", "+ Invite dispatcher")}
          </Button>
        </div>
      )}

      {inviteState.kind !== "closed" && (
        <InviteModal
          state={inviteState}
          onGenerate={onGenerate}
          onClose={onCloseInvite}
          onCopyLink={onCopyLink}
          onOpenInTelegram={onOpenInTelegram}
        />
      )}
    </List>
  );
}

// --------------------------------------------------------------------------- //
// MemberCell
// --------------------------------------------------------------------------- //

interface MemberCellProps {
  member: MemberListItem;
  isOwner: boolean;
  isSelf: boolean;
  onRemove: () => void;
}

function MemberCell({ member, isOwner, isSelf, onRemove }: MemberCellProps): React.JSX.Element {
  const { t } = useTranslation();
  const canRemove = isOwner && !isSelf && member.role !== "owner";
  const name = member.full_name ?? t("dispatcher:members.unnamed", "Unnamed");
  return (
    <Cell
      data-testid={`member-row-${member.user_id}`}
      before={
        <Avatar
          size={40}
          acronym={acronymFor(member.full_name, member.user_id)}
          data-testid={`member-avatar-${member.user_id}`}
        />
      }
      subtitle={
        <span>
          <span data-testid={`member-role-${member.user_id}`}>
            {t(`dispatcher:members.role.${member.role}`, member.role)}
          </span>
          {" · "}
          {new Date(member.joined_at).toLocaleDateString()}
        </span>
      }
      after={
        canRemove ? (
          <Button
            size="s"
            mode="plain"
            data-testid={`member-remove-${member.user_id}`}
            onClick={onRemove}
            style={{ color: "var(--tgui--destructive_text_color, #d33)" }}
          >
            {t("dispatcher:members.remove", "Remove")}
          </Button>
        ) : undefined
      }
    >
      {name}
      {isSelf && (
        <span
          data-testid={`member-self-${member.user_id}`}
          style={{ marginLeft: 8, color: "var(--tgui--hint_color, #888)", fontSize: 13 }}
        >
          {t("dispatcher:members.you", "(you)")}
        </span>
      )}
    </Cell>
  );
}

// --------------------------------------------------------------------------- //
// InviteCell
// --------------------------------------------------------------------------- //

interface InviteCellProps {
  invite: CompanyInviteResponse;
  isOwner: boolean;
  onRevoke: () => void;
}

function InviteCell({ invite, isOwner, onRevoke }: InviteCellProps): React.JSX.Element {
  const { t } = useTranslation();
  const expiresLabel = new Date(invite.expires_at).toLocaleString();
  return (
    <Cell
      data-testid={`invite-row-${invite.id}`}
      subtitle={
        <span>
          <code data-testid={`invite-hash-${invite.id}`}>{invite.token_hash_prefix}</code> ·{" "}
          <span data-testid={`invite-expires-${invite.id}`}>
            {t("dispatcher:members.expires", "Expires")}: {expiresLabel}
          </span>
        </span>
      }
      after={
        isOwner ? (
          <Button
            size="s"
            mode="plain"
            data-testid={`invite-revoke-${invite.id}`}
            onClick={onRevoke}
            style={{ color: "var(--tgui--destructive_text_color, #d33)" }}
          >
            {t("dispatcher:members.revoke", "Revoke")}
          </Button>
        ) : undefined
      }
    >
      {t(`dispatcher:members.role.${invite.target_role}`, invite.target_role)}
    </Cell>
  );
}

// --------------------------------------------------------------------------- //
// InviteModal
// --------------------------------------------------------------------------- //

interface InviteModalProps {
  state: Exclude<InviteModalState, { kind: "closed" }>;
  onGenerate: () => void;
  onClose: () => void;
  onCopyLink: (link: string) => void;
  onOpenInTelegram: (link: string) => void;
}

function InviteModal({
  state,
  onGenerate,
  onClose,
  onCopyLink,
  onOpenInTelegram,
}: InviteModalProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid="invite-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--tgui--bg_color, white)",
          borderRadius: 12,
          maxWidth: 360,
          width: "90%",
          overflow: "hidden",
        }}
      >
        {state.kind === "form" && (
          <Section header={t("dispatcher:members.invite_title", "Invite dispatcher") as string}>
            <Cell multiline>
              {t(
                "dispatcher:members.invite_hint",
                "Generate a single-use link. Share it with the person you want to add as a dispatcher.",
              )}
            </Cell>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: SPACING.sm,
                padding: SPACING.md,
              }}
            >
              <Button size="l" stretched data-testid="invite-generate-button" onClick={onGenerate}>
                {t("dispatcher:members.generate", "Generate link")}
              </Button>
              <Button
                size="l"
                mode="plain"
                stretched
                data-testid="invite-cancel-button"
                onClick={onClose}
              >
                {t("dispatcher:common.cancel", "Cancel")}
              </Button>
            </div>
          </Section>
        )}

        {state.kind === "generating" && (
          <div
            data-testid="invite-generating"
            style={{ display: "flex", justifyContent: "center", padding: 32 }}
          >
            <Spinner size="m" />
          </div>
        )}

        {state.kind === "showing_qr" && (
          <Section
            data-testid="invite-showing-qr"
            header={t("dispatcher:members.share_title", "Share invite") as string}
          >
            <div
              data-testid="invite-qr"
              style={{ display: "flex", justifyContent: "center", padding: 16 }}
            >
              <QRCodeSVG value={state.data.deep_link} size={240} level="M" />
            </div>
            <Input
              type="text"
              readOnly
              value={state.data.deep_link}
              data-testid="invite-deep-link"
              className="sd-mono"
              onFocus={(e) => e.target.select()}
            />
            <div style={{ display: "flex", gap: SPACING.sm, padding: SPACING.md }}>
              <Button
                size="l"
                mode="bezeled"
                stretched
                data-testid="invite-copy-button"
                onClick={() => onCopyLink(state.data.deep_link)}
              >
                {t("dispatcher:members.copy", "Copy")}
              </Button>
              <Button
                size="l"
                stretched
                data-testid="invite-open-tg-button"
                onClick={() => onOpenInTelegram(state.data.deep_link)}
              >
                {t("dispatcher:members.open_in_telegram", "Open in Telegram")}
              </Button>
            </div>
            <div style={{ padding: `0 ${SPACING.md}px ${SPACING.md}px` }}>
              <Button
                size="l"
                mode="plain"
                stretched
                data-testid="invite-close-button"
                onClick={onClose}
              >
                {t("dispatcher:common.close", "Close")}
              </Button>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

export default Members;
